/**
 * V.1.8.5
 * @class that creates an index table, with functionality to reorder
 * REQUIRES:
 * - MergeSort.js
 * - CreateElement.js
 * - EventDelegator.js
 */

// ************************************</editor-fold>
// IMPORTS
// ************************************<editor-fold>
// const {MergeSort} = require('../modules/MergeSort');
// const {Element} = require('../modules/createElement');
// const {EventDelegator} = require('../modules/EventDelegator');


// ************************************</editor-fold>
// TABLE
// ************************************<editor-fold>

const tableSettingsExample = {
    target: 'table', // id of/for container. OPTIONAL - Defaults to 'table'.
    sortProp: 'total', // initial sort prop. OPTIONAL - will default to first prop in data
    displayLimit: 2, // no of rows to show. OPTIONAL - will display all if not defined.
    dataOrder: ['rank', 'prop1', 'prop2', 'prop3'], // which props to include, and in which order. OPTIONAL - will use as defined in data object if not passed.
    include: { // table elements to include. OPTIONAL (as well as all props).
        auxiliaryCols: ['prop1'], // define cols that are ordered secondary to another col, i.e rankings remain matched to the primary col and no active class is attached to the rank col. OPTIONAL.
        header: { // inc. header row. OPTIONAL - will not include header if not defined.
            'rank': 'rank', // list the prop for each col to be included in the header, with it's value being the content/title. OPTIONAL - header col is excluded if not defined.
            'prop1': 'title for col 1',
            'prop2': 'title for col 2',
            /** no prop3, so no header col for prop3.*/
        },
        mobileControl: { // inc. mobile elements. OPTIONAL but also requires the header prop (above).
            titles: { // inc to override the col title as defined in header. OPTIONAL - will default to header titles above if not defined but must be defined if using images in the headers
                prop1: 'override title for col 1', // each prop will override that defined in the header. OPTIONAL - if the prop is not included here, it is excluded from the mobile control.
                /** no rank, prop2 & prop3, so only prop 1 will not be in the mobileControl in this instance */
            },
            listControlBtn: 'button text'  // include a list control button, set value to 'dynamic' to have btn text dynamically set as the currently selected prop title (defined above). OPTIONAL - no button if not defined.
        },
        toggleRowBtns: {  // inc. button that toggles visibility of rows. OPTIONAL - no button if excluded.
            showText: 'maximise', // text for button when rows are hidden. OPTIONAL - defaults to "Show All"
            hideText: 'minimise', // text for button when row is shown. OPTIONAL - deaults to "minimise".
        }
    },
    delegateEvents: true // blank table with no events delegated. OPTIONAL - defaults to true;
};

class Table extends MergeSort
{
    /**
     * @constructor
     * @param {object} sortOptions - options for sorting data, see MergeSort.js for more info. REQUIRED.
     * @param {object} tableSettings - settings for the table. OPTIONAL. takes the props in tableSettingsExample above ^
     */
    constructor(sortOptions, tableSettings)
    {
        // setup table data/settings
        super(sortOptions);
        this.assignTableSettings(tableSettings);
        this.data = this.organiseData(sortOptions.data);

        // createTableContainer
        this.findOrCreateTable();
        this.tableElements.tableBody = this.createRows();


        //create and append optional elements and the body.
        if (this.tableSettings.include)
        {
            this.tableElements.header = this.createTableHeader();


            this.tableElements.mobileControl = this.createTableHeader(true);


            this.tableElements.toggleRowBtn = this.createToggleRowBtns();
        }

        if (this.tableElements.mobileControl)
        {
            this.table.appendChild(this.tableElements.mobileControl.returnElement());
        }
        if (this.tableElements.header)
        {
            this.table.appendChild(this.tableElements.header.returnElement());
        }
        this.table.appendChild(this.tableElements.tableBody.returnElement());
        if (this.tableElements.toggleRowBtn)
        {
            this.table.appendChild(this.tableElements.toggleRowBtn.returnElement());
        }




        if (this.tableSettings.delegateEvents)
        {
            // delegate events
            new EventDelegator({delegators:[{
                selector: '#' + this.tableSettings.target,
                eventType: 'click',
                events: {
                    sort: (e, dataset) =>
                    {
                        this.sortAndFillTable(dataset.sort);
                        set_attr('mobilelistopen', false);
                    },
                    togglerows: () =>
                    {
                        this.toggleRows();
                    },
                    togglemobilecontrollist: () =>
                    {
                        this.toggleMobileControlList();
                    }
                }
            }]});

            new EventDelegator({delegators:[{
                selector: '#' + this.tableSettings.target,
                eventType: 'mouseover',
                events: {
                    mobilelistopen: (e, dataset) =>
                    {
                        if (dataset.mobilelistopen)
                        {
                            if (!e.target.classList.contains('table__mobile-control-button')
                                && !e.target.classList.contains('table__mobile-control-list')
                                && !e.target.parentNode.classList.contains('table__mobile-control-list-item')
                                && !e.target.classList.contains('table__mobile-control-list-item'))
                            {
                                this.toggleMobileControlList('force');
                            }
                        }
                    }
                }
            }]});
        }

        // sort and fill table
        this.sortAndFillTable();
        this.tableSettings.tableFilled = true;

    }


    // ======================================================================</editor-fold>
    // CONSTRUCT METHODS
    // ======================================================================<editor-fold>

    /**
     * @method table element objects consist of element and array of children,
     * this method appends an element to the target and pushes that same element to the target's arrayOfChildren
     * @param {object} element REQUIRED - element to be appended and pushed.
     * @param {object} target REQUIRED - element to be appended and pushed to.
     * @param {boolean} prepend OPTIONAL - true for prependToElement, false for appendToElement
     */
    appendAndAddtoArray(element, target, prepend)
    {
        prepend ? target.prependToElement(element.returnElement()) : target.appendToElement(element.returnElement());
        target.arrayOfChildren = target.arrayOfChildren ? target.arrayOfChildren : [];
        target.arrayOfChildren = [...target.arrayOfChildren, element];
    }

    /**
     * @method toggle the mobile control list in and out of view
     * @param {boolean / string} close - force the list to be hidden
     *        OPTIONAL - if not passed, will default to false
     * @param {string} effectIn - effect (from animate.css vendor) to be applied for bringing the list into view.
     *        OPTIONAL - if not passed, will check table settings or simply hide/show the element.
     * @param {int} durationIn - duration for animation effect in.
     *        OPTIONAL - if not passed, will check the table settings or default to 1000ms.
     * @param {string} effectOut - effect (from animate.css vendor) to be applied for bringing the list out of view.
     *        OPTIONAL - if not passed, will default back to effectIn, or check table settings.
     * @param {int} durationOut - duration for animation effect out.
     *        OPTIONAL - if not passed, will default back to durationIn, or check table settings.
     */
    toggleMobileControlList(close, effectIn, durationIn, effectOut, durationOut)
    {
        // set table operation up
        const hidden = this.tableElements.mobileControlList.element.hidden;
        const table = document.getElementById(this.tableSettings.target);
        table.setAttribute('data-mobilelistopen', close ? false : hidden);
        close ? this.tableElements.mobileControlBtn.removeClassFromElement('btn--active') :
            hidden ? this.tableElements.mobileControlBtn.addClassToElement('btn--active') : this.tableElements.mobileControlBtn.removeClassFromElement('btn--active');

        // define effects from parameters passed, else the tableSettings. If no effect Out, use effect In.
        const animationSettings = (this.tableSettings.include.mobileControl.listControlBtn && this.tableSettings.include.mobileControl.listControlBtn.animation) ? this.tableSettings.include.mobileControl.listControlBtn.animation : false;
        const eIn = effectIn ? effectIn : animationSettings ? animationSettings.effectIn : false;
        const eOut = effectOut ? effectOut : effectIn ? effectIn : !animationSettings ? eIn : animationSettings.effectOut ? animationSettings.effectOut : animationSettings.effectIn;

        // define durations from parameters passed, else the tableSettings. If no duration out, forceClose (no effect)
        const dIn = durationIn ? durationIn : animationSettings ? animationSettings.durationIn : 1000;
        const dOut = durationOut ? durationOut : durationIn ? durationIn : !animationSettings ? false : animationSettings.durationOut ? animationSettings.durationOut : animationSettings.durationIn;
        const forceClose = (close && (typeof close === 'string') && (close === 'force')) ? true : (!hidden && !dOut);

        // toggle the animation and closing of the list
        if (eIn && !forceClose)
        {
            const effect = hidden ? eIn : eOut;
            const duration = hidden ? dIn : dOut;

            this.tableElements.mobileControlList.animateTheElementInOrOut(effect, duration, close);
        }
        else
        {
            this.tableElements.mobileControlList.toggleTheElement(true, forceClose);
        }
    }

    /**
     * @method assign the passed table settings to the table, applying default values where required.
     * @param {object} options - table settings, will apply defaults if not passed. OPTIONAL.
     */
    assignTableSettings(options)
    {
        const keys = Object.keys(this.data[0]);
        const defaultTableOptions = {
            target: 'table',
            dataOrder: keys,
            sortProp: keys[0],
            descending: this.rankHighToLow,
            ranking: keys.includes('rank'),
            noOfRows: this.data.length,
            noOfCols: keys.length,
            assignRowTo: keys[0],
            assignedAttributes: {
                attributes: [],
                targets: []
            },
            delegateEvents: true
        };

        if (!options)
        {
            options = defaultTableOptions;
        }
        else
        {
            if (!options.target)
            {
                options.target = defaultTableOptions.target;
            }
            if (!options.sortProp)
            {
                options.sortProp = options.dataOrder ? options.dataOrder[0] : defaultTableOptions.sortProp;
            }
            if (!options.dataOrder)
            {
                options.dataOrder = defaultTableOptions.dataOrder;
            }
            if (!options.assignRowTo)
            {
                options.assignRowTo = defaultTableOptions.assignRowTo;
            }
            if (options.delegateEvents === undefined)
            {
                options.delegateEvents = defaultTableOptions.delegateEvents;
            }

            options.descending = defaultTableOptions.descending;
            options.ranking = defaultTableOptions.ranking;
            options.noOfRows = defaultTableOptions.noOfRows;
            options.noOfCols = defaultTableOptions.noOfCols;
            options.assignedAttributes = defaultTableOptions.assignedAttributes;
        }

        if (options.include && options.include.toggleRowBtns)
        {
            const OptionsTglRowBtns = options.include.toggleRowBtns;
            OptionsTglRowBtns.showText = OptionsTglRowBtns.showText ? OptionsTglRowBtns.showText : 'Show All';
            OptionsTglRowBtns.hideText = OptionsTglRowBtns.hideText ? OptionsTglRowBtns.hideText : 'Minimise';
        }

        options.initialSortProp = options.sortProp;
        this.tableSettings = options;
    }

    /**
     * @method create cols in row
     * @param {object} row - target row element object.
     */
    createColsInRow(row)
    {
        let c;
        row.arrayOfChildren = [];
        for (c = 1; c <= this.tableSettings.noOfCols; c++)
        {
            const col = new Element({classes: 'table__col'});
            this.appendAndAddtoArray(col, row);
        }
    }

    /**
     * @method create the table rows
     */
    createRows()
    {
        // create container
        const tableBody = new Element({id: 'table__body', classes: 'table__body'});
        tableBody.arrayOfRowIDs = [];
        tableBody.arrayOfChildren = [];

        let r;
        for (r = 0; r < this.tableSettings.noOfRows; r++)
        {
            // create row
            const rowID = this.data[r][this.tableSettings.assignRowTo];
            const tableRow = new Element({
                classes: 'table__row table__row--body',
                attributions: {attribute: 'data-target', value: rowID}
            });
            tableRow.rowID = rowID;
            tableBody.arrayOfRowIDs = [...tableBody.arrayOfRowIDs, rowID];

            // create cols in row
            this.createColsInRow(tableRow);

            // hide and add class hidden on rows over the display limit
            if (this.tableSettings.displayLimit && ((r + 1) > this.tableSettings.displayLimit))
            {
                tableRow.addClassToElement('table__row--hidden');
                tableRow.hideTheElement();
            }

            this.appendAndAddtoArray(tableRow, tableBody);
        }

        return tableBody;
    }

    /**
     * @method create Table Headers (and optional mobile elements).
     * @param {boolean} mobile - create mobile table header elements . OPTIONAL.
     */
    createTableHeader(mobile)
    {
        if (mobile && !this.tableSettings.include.mobileControl)
        {
            return false;
        }

        if (this.tableSettings.include.header)
        {
            // create container
            const container = new Element({
                element: mobile ? 'ul' : 'div',
                classes: mobile ? 'table__mobile-control-list' : 'table__row table__row--header',
                id: mobile ? `${this.tableSettings.target}__mobile-control-list` : `${this.tableSettings.target}__row--header`
            });

            // loop header col props
            for (const prop of this.tableSettings.dataOrder)
            {
                if (this.tableSettings.include.header[prop])
                {
                    // create header col
                    let title;
                    if (mobile && this.tableSettings.include.mobileControl.titles)
                    {
                        if (this.tableSettings.include.mobileControl.titles[prop])
                        {
                            title = this.returnStringOrImg(this.tableSettings.include.mobileControl.titles[prop]);
                        }
                        else
                        {
                            continue;
                        }
                    }
                    else
                    {
                        title = this.returnStringOrImg(this.tableSettings.include.header[prop]);
                    }


                    const propClass = prop.replace(' ', '-');
                    const item = new Element({
                        element: mobile ? 'li' : 'div',
                        classes: mobile ?
                            'table__mobile-control-list-item table__col--' + propClass
                            :
                            'table__col table__col--' + propClass,
                        content: title,
                        attributions: {attribute: 'data-sort', value: prop}
                    });


                    // set or unset  active and ascending/descending class
                    (prop !== this.tableSettings.sortProp) ? item.removeClassFromElement('table__col--active table__col--ascending table__col--descending', false) : this.tableSettings.descending ?
                        item.addClassToElement('table__col--active table__col--ascending') : item.addClassToElement('table__col--active table__col--descending');

                    this.appendAndAddtoArray(item, container, false);
                }
            }

            if (mobile)
            {
                // create mobile wrapper
                const wrapper = new Element({
                    element: 'div',
                    classes: 'table__mobile-control',
                    id: 'table__mobile-control'
                });

                // create list control btn, if to be included
                if (this.tableSettings.include.mobileControl.listControlBtn)
                {
                    const listControlBtnText = this.tableSettings.include.mobileControl.listControlBtn.text;
                    if ((typeof this.tableSettings.include.header[this.tableSettings.sortProp] === 'object') && (!this.tableSettings.include.mobileControl.titles))
                    {
                        console.warn('tableSettings.include.mobileControl.titles must be defined when using mobileControl and an image in the headers');
                    }

                    const button = new Element({
                        element: 'button',
                        classes: 'table__mobile-control-button',
                        id: 'table__mobile-control-button',
                        content: (listControlBtnText !== 'dynamic') ?
                            listControlBtnText : ((this.tableSettings.include.mobileControl.titles) && (this.tableSettings.include.mobileControl.titles[this.tableSettings.sortProp])) ?
                                this.returnStringOrImg(this.tableSettings.include.mobileControl.titles[this.tableSettings.sortProp])
                                :
                                this.returnStringOrImg(this.tableSettings.include.header[this.tableSettings.sortProp]),
                        attributions: {
                            attribute: 'data-togglemobilecontrollist'
                        }
                    });
                    container.hideTheElement();
                    this.appendAndAddtoArray(button, wrapper, false);
                    button.dynamic = (listControlBtnText === 'dynamic');
                    this.tableElements.mobileControlBtn = button;
                }

                // add the container to the wrapper
                this.appendAndAddtoArray(container, wrapper, false);
                this.tableElements.mobileControlList = container;

                return wrapper;
            }
            else
            {
                return container;
            }
        }
        else
        {
            if (mobile)
            {
                console.warn('for mobile control to be created, header rows must be defined in tableSettings.include.header')
            }
        }
        return false;
    }

    /**
     * @method create toggle row btn at the end of the table
     */
    createToggleRowBtns()
    {
        if (this.tableSettings.displayLimit)
        {
            // setup settings for when ToggleRowBtn is clicked
            this.tableSettings.allRowsVisible = false;

            // create btn (and container), if to be included
            if (this.tableSettings.include && this.tableSettings.include.toggleRowBtns)
            {
                let toggleRowsBtnContent = this.tableSettings.include.toggleRowBtns.showText;
                toggleRowsBtnContent = this.returnDataType(toggleRowsBtnContent) === 'img' ?
                    new Element({element: 'IMG', attributions: {attribute: 'src', value: toggleRowsBtnContent}}) :
                    toggleRowsBtnContent;
                const toggleRowsBtnContainer = new Element({classes: 'toggle-rows-btn'});
                const toggleRowsBtn = new Element({
                    element: 'button',
                    classes: 'toggle-rows-btn__btn',
                    id: 'toggle-rows-btn__btn',
                    content: toggleRowsBtnContent,
                    attributions: [{attribute: 'data-togglerows'}]
                });

                toggleRowsBtnContainer.appendToElement(toggleRowsBtn.returnElement());
                return toggleRowsBtnContainer;
            }
        }
        else
        {
            this.tableSettings.allRowsVisible = true;
        }

        return false;
    }

    /**
     * @method  find target element for table, or create it if it does not exist.
     */
    findOrCreateTable()
    {
        this.tableElements = {};
        this.table = document.getElementById(this.tableSettings.target);
        if (!this.tableSettings.target || !this.table)
        {
            this.table = new Element({
                id: this.tableSettings.target,
                classes: this.tableSettings.target
            }).returnElement();
            console.warn('table constructor: target does not exist - manually appendToElement to DOM');
        }
        else
        {
            while (this.table.firstChild)
            {
                this.table.removeChild(this.table.firstChild);
            }
        }
    }

  /**
     * @method organise the data as defined in the options.dataOrder, if included.
     * The order the props appear in the array, is the order their respective columns will be in.
     * If this prop is included, any props not in the array will be excluded
     * @param {object} data - data to organise. REQUIRED.
     * @param {object} options - table settings, using the include.data prop, if it exists. REQUIRED.
     */
    organiseData(data)
    {
        let tableDataArray, prevObj;
        tableDataArray = [];
        for (const obj of data)
        {
            const newObj = {};
            for (const dataToInclude of this.tableSettings.dataOrder)
            {
                for (const prop in obj)
                {
                    if (dataToInclude === prop)
                    {
                        newObj[prop] = obj[prop];
                        if (prevObj && (typeof newObj[prop] !== typeof prevObj[prop]) && (this.empty_value !== newObj[prop]) && (this.empty_value !== prevObj[prop]))
                        {
                            this.throwError(`you have ${typeof newObj[prop]}s mixed in with ${typeof prevObj[prop]}s in data.${prop},(which aren't the defined 'empty_value for the MergeSort Params) - this will break the table on reorder. Make sure each prop's data is of the same type or set the 'empty_value' prop in the MergeSort options.`)
                        }
                    }
                }
            }
            tableDataArray = [...tableDataArray, newObj];

            prevObj = newObj;
        }
        return tableDataArray;
    }

    /**
     * @method set the active class on the active header col
     * @param {string} propToSortBy - the current sort prop
     * @param {boolean} mobile - target mobile header elements or not.
     */
    setActiveClassOnHeaders(propToSortBy, mobile)
    {
        let auxiliaryCol;
        if (this.tableSettings.include && this.tableSettings.include.auxiliaryCols && this.tableSettings.include.auxiliaryCols.includes(propToSortBy))
        {
            auxiliaryCol = true;
        }

        const prop = mobile ? 'mobileControlList' : 'header';
        for (const col of this.tableElements[prop].arrayOfChildren)
        {

            // reset header col

            auxiliaryCol ? col.removeClassFromElement('table__col--descending table__col--ascending') : col.removeClassFromElement('table__col--active table__col--descending table__col--ascending');


            // apply active class if current
            if (col.element.getAttribute('data-sort') === propToSortBy)
            {
                if (!auxiliaryCol)
                {
                    col.addClassToElement('table__col--active ');
                }
                this.tableSettings.descending ? col.addClassToElement('table__col--ascending') : col.addClassToElement('table__col--descending');
            }
        }

    }

    /**
     * @method  sort the data then fill the table
     * @param {object} dataToSort  - data to sort. OPTIONAL.
     */
    sortAndFillTable(dataToSort)
    {
        if (dataToSort)
        {
            // set sort and descending variable;
            // depending on whether the current sort prop matches the previous or not
            if (dataToSort === this.tableSettings.sortProp)
            {
                this.tableSettings.descending = !this.tableSettings.descending;
            }
            else
            {
                this.tableSettings.descending = this.rankHighToLow;
                this.tableSettings.sortProp = dataToSort;
            }

            // set header classes etc for new prop
            if (this.tableElements.header)
            {
                this.setActiveClassOnHeaders(dataToSort);
                if (this.tableElements.mobileControl)
                {
                    this.setActiveClassOnHeaders(dataToSort, true);
                    if (this.tableElements.mobileControlBtn)
                    {
                        this.tableElements.mobileControlList.hideTheElement();
                        if (this.tableElements.mobileControlBtn.dynamic)
                        {
                            if (this.tableSettings.include.mobileControl.titles)
                            {
                                if (this.tableSettings.include.mobileControl.titles[dataToSort])
                                {
                                    this.tableElements.mobileControlBtn.updateContentInElement(this.returnStringOrImg(this.tableSettings.include.mobileControl.titles[dataToSort]))
                                }
                            }
                            else
                            {
                                this.tableElements.mobileControlBtn.updateContentInElement(this.tableSettings.include.header[dataToSort]);
                            }
                        }
                    }
                }
            }
        }

        this.fillTable();
    }

    // ======================================================================</editor-fold>
    // GENERAL METHODS
    // ======================================================================<editor-fold>
    /**
     * @method add attributions to table elements
     * @param {string} targetElement - prop of the target element in this.tableElements. REQUIRED.
     * @param {object / array} attributions - object or array of objects containing attribute and value (optional) props as strings. REQUIRED
     * @param {string / array} childTarget - if passed, the array of children will be searched to apply the attributes to.
     * If targeting a row, this will be the row's data-target attribute, otherwise it's a class.
     * Separate multiple targets with a space " " or pass as array.
     * Set as 'all' to target all the children.
     * OPTIONAL.
     */
    addAttributesToElement(targetElement, attributions, childTarget)
    {
        if (this.tableElements[targetElement])
        {
            if (childTarget)
            {
                if (typeof childTarget === 'string')
                {
                    childTarget = childTarget.split(' ');
                }

                if (childTarget.length > 1)
                {
                    for (const targetClass of childTarget)
                    {
                        this.addAttributesToElement(targetElement, attributions, [targetClass])
                    }
                }
                else
                {
                    // if child target, loop children to wrap
                    for (const child of this.tableElements[targetElement].arrayOfChildren)
                    {
                        // if all, wrap all elements
                        if (childTarget[0] === 'all')
                        {
                            child.addOrUpdateMultipleAttributesToElement(attributions);
                        }
                        else
                        {
                            // if tablebody (finding rows), match rowID rather than class
                            if (targetElement === 'tableBody')
                            {
                                if (child.rowID === childTarget)
                                {
                                    child.addOrUpdateMultipleAttributesToElement(attributions);
                                    break;
                                }
                            }
                            else if (child.elementHasClass(childTarget))
                            {
                                child.addOrUpdateMultipleAttributesToElement(attributions);
                                break;
                            }
                        }
                    }
                }
            }
            else
            {
                this.tableElements[targetElement].addOrUpdateMultipleAttributesToElement(attributions);
            }
        }
        else
        {
            this.throwError('addAttributeToElement: element does not exist. ()')
        }

        this.fillTable();
    }

    /**
     * @method add Attributes to all cols in table body
     * @param {object} attributions - object or array of objects containing attribute and value (optional) props as strings. REQUIRED.
     * @param {array / string} targetClasses - add attribute only to these classes.
     * - accepts and array of classes, a single class or a list of classes as a string (each class to be separated by a space).
     * OPTIONAL.
     */
    addAttributesToCols(attributions, targetClasses)
    {
        if (targetClasses && typeof targetClasses === 'string')
        {
            targetClasses = targetClasses.split(' ');
        }

        if (targetClasses && targetClasses.length > 1)
        {
            for (const targetClass of targetClasses)
            {
                this.addAttributesToCols(attributions, [targetClass])
            }
        }
        else
        {
            const targetClass = targetClasses;
            for (const row of this.tableElements.tableBody.arrayOfChildren)
            {
                for (const col of row.arrayOfChildren)
                {
                    if ((targetClass && col.elementHasClass(targetClass)) || !targetClass)
                    {
                        col.addOrUpdateMultipleAttributesToElement(attributions);
                    }
                }
            }
        }
    }

    /**
     * @method fill table with data
     */
    fillTable()
    {
        // order the data
        const staticRanks = (this.tableSettings.include && this.tableSettings.include.staticRankCols && this.tableSettings.include.staticRankCols.includes(this.tableSettings.sortProp));
        const auxiliaryCol = (this.tableSettings.include && this.tableSettings.include.auxiliaryCols && this.tableSettings.include.auxiliaryCols.includes(this.tableSettings.sortProp));
        const initialSortProp = {
            propToSort: this.tableSettings.initialSortProp,
            dataType: this.returnDataType(this.data[0][this.tableSettings.initialSortProp])
        };
        const currentSortProp = {
            propToSort: this.tableSettings.sortProp,
            dataType: this.returnDataType(this.data[0][this.tableSettings.sortProp])
        };
        const rankOptions = {
            update_ranks: staticRanks ? initialSortProp : auxiliaryCol ? false : currentSortProp,
            static_ranks: (staticRanks || auxiliaryCol) ? currentSortProp : false
        };
        const sortedArray = this.tableSettings.descending ? this.highToLow(rankOptions) : this.lowToHigh(rankOptions);

        for (const d in sortedArray)
        {
            // get the row and id, and assign attribute to target
            const row = this.tableElements.tableBody.arrayOfChildren[d];
            const rowID = sortedArray[d][this.tableSettings.assignRowTo];
            row.addOrUpdateAttributeToElement('data-target', rowID);

            // loop assigned attributes and apply
            if ((this.tableSettings.assignedAttributes.attributes.length > 0))
            {
                // reset row
                for (const attr of this.tableSettings.assignedAttributes.attributes)
                {
                    (attr !== 'class') ?
                        row.element.removeAttribute(attr) : row.elementHasClass('table__row--hidden') ?
                        row.removeClassFromElement('table__row table__row--body table__row--hidden', true) : row.removeClassFromElement('table__row table__row--body', true);
                }

                // apply assigned attributes that apply to current row
                if ((this.tableSettings.assignedAttributes.targets.length > 0) && this.tableSettings.assignedAttributes.targets.includes(rowID))
                {
                    for (const attr of this.tableSettings.assignedAttributes[rowID])
                    {
                        (attr.attribute !== 'class') ?
                            row.addOrUpdateAttributeToElement(attr.attribute, attr.value) : attr.value ?
                            row.addClassToElement(attr.value) : row.addClassToElement('');
                    }
                }
            }
            else
            {
                row.elementHasClass('table__row--hidden') ? row.removeClassFromElement('table__row table__row--body table__row--hidden', true) : row.removeClassFromElement('table__row table__row--body', true);
            }

            const listOfColsInRow = row.arrayOfChildren;
            let i;
            i = 0;
            for (const prop in sortedArray[d])
            {
                // exclude if ranking not included in settings
                if (!this.tableSettings.ranking && prop === 'rank')
                {
                    continue;
                }

                // set the col content to new data
                const content = (this.returnDataType(sortedArray[d][prop]) === 'date') ?
                    this.formatDate(sortedArray[d][prop])
                    :
                    (this.returnDataType(sortedArray[d][prop]) === 'img') ?
                        new Element({element: 'IMG', attributions: {attribute: 'src', value: sortedArray[d][prop]}})
                        :
                        sortedArray[d][prop];
                listOfColsInRow[i].updateContentInElement(`${content}`);

                // wrap all cols if set to wrap all cols
                if (this.tableSettings.wrapCols && listOfColsInRow[i].elementHasClass('col--wrapped'))
                {
                    listOfColsInRow[i].wrapContentOfElement(this.tableSettings.wrapCols)

                }

                // set active class if current col
                if (!auxiliaryCol)
                {
                    (prop === this.tableSettings.sortProp) ? listOfColsInRow[i].addClassToElement('table__col--active') : listOfColsInRow[i].removeClassFromElement('table__col--active');
                }

                // on initiation, set a unique class to col
                if (!this.tableSettings.tableFilled)
                {
                    listOfColsInRow[i].addClassToElement('table__col--' + prop.replace(' ', '-'));
                }

                i++;
            }
        }
    }

    /**
     * @method format string to date
     * @param {string} dateString - string in YYYY-MM-DD format.
     */
    formatDate(dateString)
    {
        const date = new Date(dateString);
        const monthNames = [
            'January', 'February', 'March',
            'April', 'May', 'June', 'July',
            'August', 'September', 'October',
            'November', 'December'
        ];
        const day = date.getDate();
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        return day + ' ' + monthNames[monthIndex] + ' ' + year;
    }

    returnStringOrImg(data)
    {
        return (this.returnDataType(data) === 'img')
            ? new Element({
                element: 'IMG',
                attributions: {
                    attribute: 'src',
                    value: data
                }
            }) : data;
    }

    /**
     * @method return the data type: number, string or date (string in YYYY-MM-DD) format;
     * @param {int / string} data - data to check
     */
    returnDataType(data)
    {
        return (((typeof data) === 'string') && (/([012]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/.test(data)))
            ? 'date' : (((typeof data) === 'string') && (data.indexOf('assets/img/') !== -1))
                ?
                'img' : (typeof data);
    }

    /**
     * @method assign attribute to Row, so it stays attached to the row when table is reordered
     * @param {string} rowID - row ID as defined in the rows data-target attribute. OPTIONAL - if not passed, all rows will be assigned the attribute.
     * @param {string} attribute - attribute to add. REQUIRED.
     * @param {string} value - value of the attribute to add. OPTIONAL.
     * @param {boolean} unique - remove all other instances of the attribute. OPTIONAL.
     * @param {boolean} isCallback - defines if this instance is a callback. VOID - only passed in callback.
     */
    assignAttributeToRow(rowID, attribute, value, unique, isCallback = false)
    {
        if (!rowID)
        {
            for (const row of this.tableElements.tableBody.arrayOfRowIDs)
            {
                this.assignAttributeToRow(row, attribute, value, false, true);
            }
        }
        else if (attribute === 'data-target')
        {
            this.throwError('assignAttributeToRow: not allowed to assign data-target')
        }
        else if (!this.tableElements.tableBody.arrayOfRowIDs.includes(rowID))
        {
            this.throwError('assignAttributeToRow: row does not exist')
        }
        else
        {
            if (unique)
            {
                (attribute === 'class' && value) ? this.unassignAttributeFromRow(false, attribute, value) : this.unassignAttributeFromRow(false, attribute);
            }

            // add attribute and target to list of attributes/targets in settings
            this.tableSettings.assignedAttributes.attributes = this.tableSettings.assignedAttributes.attributes.includes(attribute) ? this.tableSettings.assignedAttributes.attributes : [...this.tableSettings.assignedAttributes.attributes, attribute];
            this.tableSettings.assignedAttributes.targets = this.tableSettings.assignedAttributes.targets.includes(rowID) ? this.tableSettings.assignedAttributes.targets : [...this.tableSettings.assignedAttributes.targets, rowID];

            // if class and value, add new classes to previous rather than overwrite.
            if (this.tableSettings.assignedAttributes[rowID] && (attribute === 'class' && value))
            {
                for (const attr of this.tableSettings.assignedAttributes[rowID])
                {
                    if (attr.attribute === attribute)
                    {
                        const prevVal = attr.value ? attr.value : false;
                        const newValArray = value.split(' ');
                        for (const v of newValArray)
                        {
                            value = !prevVal ?
                                v : prevVal.includes(v) ?
                                    prevVal : prevVal + ' ' + v;
                        }
                        break;
                    }
                }
            }

            // add assigned attribute if it doesn't exist, overwrite if it does.
            this.tableSettings.assignedAttributes[rowID] = this.tableSettings.assignedAttributes[rowID] ? this.tableSettings.assignedAttributes[rowID].filter((item) => item.attribute !== attribute) : [];
            const attributeToAdd = value ? {attribute: attribute, value: value} : {attribute: attribute};
            this.tableSettings.assignedAttributes[rowID] = [...this.tableSettings.assignedAttributes[rowID], attributeToAdd];
        }

        // fill table; prevent unnecessary runs by only firing on initial instance.
        if (!isCallback)
        {
            this.fillTable();
        }
    }

    /**
     * @method assign a class to a row, so it stays attached to the row when table is reordered
     * @param {string} classes - classes to attach, each seperated by a space. REQUIRED.
     * @param {string} rowID - row ID as defined in the rows data-target attribute. OPTIONAL - if not passed, all rows will be assigned the classes.
     * @param {boolean} unique - remove all other instances of the attribute. OPTIONAL.
     */
    assignClassToRow(classes, rowID, unique)
    {
        classes = !classes ? this.throwError('assignClassToRow: no classes passed') : (typeof classes === 'string') ?
            classes.split(' ') : classes;

        for (const singleClass of classes)
        {
            this.assignAttributeToRow(rowID, 'class', singleClass, unique);
        }
    }

    /**
     * @method toggle display of column rows; only applicable if displayLimit is defined in the table settings
     * @param {element/string} btn - pass the button to update the text of that btn (usually it will be 'this'). alternatively, pass the ID as a string. OPTIONAL.
     * @param {string} showBtnText - text the button will show when rows are hidden. OPTIONAL.
     * @param {string} hideBtnText - text the button will show when all rows are visible. OPTIONAL.
     */
    toggleRows(btn, showBtnText, hideBtnText)
    {
        // loop rows and hide or show
        for (const row of this.tableElements.tableBody.arrayOfChildren)
        {

            if (row.elementHasClass('table__row--hidden'))
            {
                (this.tableSettings.allRowsVisible) ? row.hideTheElement() : row.showTheElement(true);
            }
        }

        // toggle btn text
        if (btn || (this.tableSettings.include && this.tableSettings.include.toggleRowBtns))
        {
            btn = !btn ? document.getElementById('toggle-rows-btn__btn') : (typeof btn === 'string') ? document.getElementById(btn) : btn;

            showBtnText = showBtnText ? showBtnText : this.tableSettings.include.toggleRowBtns ? this.tableSettings.include.toggleRowBtns.showText : 'Show All';
            showBtnText = this.returnDataType(showBtnText) === 'img' ?
                new Element({element: 'IMG', attributions: {attribute: 'src', value: showBtnText}}).returnElement() :
                document.createTextNode(showBtnText);

            hideBtnText = hideBtnText ? hideBtnText : this.tableSettings.include.toggleRowBtns ? this.tableSettings.include.toggleRowBtns.hideText : 'Minimize';
            hideBtnText = this.returnDataType(hideBtnText) === 'img' ?
                new Element({element: 'IMG', attributions: {attribute: 'src', value: hideBtnText}}).returnElement() :
                document.createTextNode(hideBtnText);

            (this.tableSettings.allRowsVisible) ? btn.classList.remove('toggle-rows-btn__btn--expanded') : btn.classList.add('toggle-rows-btn__btn--expanded');
            btn.innerHTML = '';
            this.tableSettings.allRowsVisible ? btn.appendChild(showBtnText) : btn.appendChild(hideBtnText);

        }

        // record for next click
        this.tableSettings.allRowsVisible = !this.tableSettings.allRowsVisible;
    }

    /**
     * @method remove the assignment of a class to a row
     * @param {string} classes - classes to attach, each seperated by a space. OPTIONAL - if not passed, all assigned classes will be removed.
     * @param {string} rowID - row ID as defined in the rows data-target attribute. OPTIONAL - if not passed, the classes will be removed from all rows.
     */
    unassignClassFromRow(classes, rowID)
    {
        classes = !classes ? [false] : (typeof classes === 'string') ?
            classes.split(' ') : classes;

        for (const singleClass of classes)
        {
            this.unassignAttributeFromRow(rowID, 'class', singleClass);
        }
    }

    /**
     * @method remove the assignment of an attribute to a row
     * @param {string} rowID - row ID as defined in the rows data-target attribute. OPTIONAL - if not passed, all rows will be unassigned the attribute.
     * @param {string} attribute - attribute to remove. REQUIRED.
     * @param {string} value - if included, only the attribute with a matching value will be removed. OPTIONAL.
     * @param {boolean} isCallback - defines if this instance is a callback. VOID - only passed in callback.
     */
    unassignAttributeFromRow(rowID, attribute, value, isCallback = false)
    {
        let uniqueVal;
        uniqueVal = true;
        if (!rowID)
        {
            for (const row of this.tableElements.tableBody.arrayOfRowIDs)
            {
                this.unassignAttributeFromRow(row, attribute, value, true);
            }
        }
        else if (attribute === 'data-target')
        {
            this.throwError('unassignAttributeFromRow: not allowed to assign data-target')
        }
        else if (!this.tableElements.tableBody.arrayOfRowIDs.includes(rowID))
        {
            this.throwError('unassignAttributeFromRow: row does not exist')
        }
        else if (this.tableSettings.assignedAttributes[rowID])
        {
            for (const obj of this.tableSettings.assignedAttributes[rowID])
            {
                if (value)
                {
                    if (obj.attribute === attribute)
                    {
                        if (obj.value || obj.value !== '')
                        {
                            // if class, add to previous value rather than overwrite.
                            if (attribute === 'class')
                            {
                                const prevVal = obj.value;
                                let prevValArray;
                                let newVal;
                                const newValArray = value.split(' ');

                                prevValArray = prevVal.split(' ');
                                newVal = '';
                                for (const val of newValArray)
                                {
                                    prevValArray = prevValArray.filter((item) => item !== val);
                                }
                                for (const val of prevValArray)
                                {
                                    newVal += ' ' + val;
                                }

                                obj.value = newVal;

                                if (newVal === '' || newVal === ' ')
                                {
                                    this.tableSettings.assignedAttributes[rowID] = this.tableSettings.assignedAttributes[rowID].filter((item) => item.attribute !== attribute);
                                }
                                else
                                {
                                    uniqueVal = false;
                                }
                            }
                            // if value and attribute match, remove.
                            else if (obj.value === value)
                            {
                                const filters = {attribute: attribute, value: value};
                                this.tableSettings.assignedAttributes[rowID] = this.tableSettings.assignedAttributes[rowID].filter((obj) => obj.attribute !== filters.attribute && obj.value !== filters.value);
                            }
                            // if there is an attribute with a value that does not match, it is not unique
                            else
                            {
                                uniqueVal = false;
                            }
                        }
                        // if attribute has no value, remove
                        else
                        {
                            this.tableSettings.assignedAttributes[rowID] = this.tableSettings.assignedAttributes[rowID].filter((item) => item.attribute !== attribute);
                        }
                    }
                }
                // remove attribute, no value condition to check.
                else
                {
                    this.tableSettings.assignedAttributes[rowID] = this.tableSettings.assignedAttributes[rowID].filter((item) => item.attribute !== attribute);
                }
            }

            // if the attribute (or attribute/value combination) is unique, remove it from the array of assigned attributes.
            if (uniqueVal)
            {
                this.tableSettings.assignedAttributes.attributes = this.tableSettings.assignedAttributes.attributes.filter((item) => item !== attribute);
            }

            // if there are no attributes for the rowID, remove it from the array of targets
            if (this.tableSettings.assignedAttributes[rowID].length === 0)
            {
                this.tableSettings.assignedAttributes.targets = this.tableSettings.assignedAttributes.targets.filter((item) => item !== rowID);
            }
        }

        // fill table; prevent unnecessary runs by only firing on initial instance.
        if (!isCallback)
        {
            this.fillTable();
        }
    }

    /**
     * @method wrap table elements in another targetElement
     * @param {string} targetElement - prop of the target element in this.tableElements. REQUIRED.
     * @param {object} wrapperOptions - attributes etc for the wrapper element - see wrapContentOfElement method in createElement.js for more info. OPTIONAL.
     * @param {string / array} childTarget - if passed, the array of children will be searched to apply the wrap to.
     * If targeting a row, this will be the row's data-target attribute, otherwise it's a class.
     * Separate multiple targets with a space " " or pass as array.
     * Set as 'all' to target all the children.
     * OPTIONAL.
     */
    wrapElement(targetElement, wrapperOptions, childTarget)
    {
        if (this.tableElements[targetElement])
        {
            if (childTarget)
            {
                if (typeof childTarget === 'string')
                {
                    childTarget = childTarget.split(' ');
                }

                if (childTarget.length > 1)
                {
                    for (const targetClass of childTarget)
                    {
                        this.wrapElement(targetElement, wrapperOptions, [targetClass])
                    }
                }
                else
                {
                    // if child target, loop children to wrap
                    for (const child of this.tableElements[targetElement].arrayOfChildren)
                    {
                        // if all, wrap all elements
                        if (childTarget === 'all')
                        {
                            child.wrapContentOfElement(wrapperOptions);
                        }
                        else
                        {
                            // if tablebody (finding rows), match rowID rather than class
                            if (targetElement === 'tableBody')
                            {
                                if (child.rowID === childTarget)
                                {
                                    child.wrapContentOfElement(wrapperOptions);
                                    break;
                                }
                            }
                            else if (child.elementHasClass(childTarget))
                            {
                                child.wrapContentOfElement(wrapperOptions);
                                break;
                            }
                        }
                    }
                }
            }
            else
            {
                this.tableElements[targetElement].wrapContentOfElement(wrapperOptions);
            }
        }
        else
        {
            this.throwError('wrapElement: element does not exist. ()')
        }

        this.fillTable();
    }

    /**
     * @method wrap all the cols in table body
     * @param {object} wrapperOptions - attributes etc for the wrapper element - see wrapContentOfElement method in createElement.js for more info. OPTIONAL.
     * @param {array / string} targetClasses - apply wraps only to these classes.
     * - accepts and array of classes, a single class or a list of classes as a string (each class to be separated by a space).
     * OPTIONAL.
     */
    wrapCols(wrapperOptions, targetClasses)
    {
        if (targetClasses && typeof targetClasses === 'string')
        {
            targetClasses = targetClasses.split(' ');
        }

        if (targetClasses && targetClasses.length > 1)
        {
            for (const targetClass of targetClasses)
            {
                this.wrapCols(wrapperOptions, [targetClass])
            }
        }
        else
        {
            const targetClass = targetClasses;
            for (const row of this.tableElements.tableBody.arrayOfChildren)
            {
                for (const col of row.arrayOfChildren)
                {
                    if ((targetClass && col.elementHasClass(targetClass)) || !targetClass)
                    {
                        col.addClassToElement('col--wrapped');
                        col.wrapContentOfElement(wrapperOptions);
                        this.tableSettings.wrapCols = wrapperOptions ? wrapperOptions : {};
                    }
                }
            }
        }
    }

    /**
     * @method appendToElement an item to a table element
     * @param {string} targetElement - prop under this.tableElements to target. REQUIRED.
     * @param {node} item - item to appendToElement. REQUIRED.
     * @param {boolean} prepend - whether to prependToElement or appendToElement. OPTIONAL.
     * @param {string} childTarget - if passed, the array of children will be searched to appendToElement the item to.
     * If targeting a row, this will be the row's data-target attribute, otherwise it's a class.
     * set as 'all' to target all the children.
     * OPTIONAL.
     */
    appendtoElement(targetElement, item, prepend, childTarget)
    {
        if (this.tableElements[targetElement])
        {
            if (childTarget)
            {
                if (typeof childTarget === 'string')
                {
                    childTarget = childTarget.split(' ');
                }

                if (childTarget.length > 1)
                {
                    for (const targetClass of childTarget)
                    {
                        this.addAttributesToElement(targetElement, attributions, [targetClass])
                    }
                }
                else
                {
                    // if child target, loop children to wrap
                    for (const child of this.tableElements[targetElement].arrayOfChildren)
                    {
                        const duplicate = item.cloneNode(true);
                        // if all, wrap all elements
                        if (childTarget === 'all')
                        {
                            prepend ? child.prependToElement(duplicate) : child.appendToElement(duplicate);
                        }
                        else
                        {
                            // if tablebody (finding rows), match rowID rather than class
                            if (targetElement === 'tableBody')
                            {
                                if (child.rowID === childTarget)
                                {
                                    prepend ? child.prependToElement(duplicate) : child.appendToElement(duplicate);
                                    break;
                                }
                            }
                            else if (child.elementHasClass(childTarget))
                            {
                                prepend ? child.prependToElement(duplicate) : child.appendToElement(duplicate);
                                break;
                            }
                        }
                    }
                }
            }
            else
            {
                prepend ? this.tableElements[targetElement].prependToElement(item) : this.tableElements[targetElement].appendToElement(item);
            }
        }
        else
        {
            this.throwError('wrapElement: element does not exist. ()')
        }
    }

    /**
     * @method add bootsrap grid classes to the col
     * @param {obj} colWidths - all the col widths (number out of 12). accepts props xs, sm, md, lg and xl. OPTIONAL.
     * @param {string} extraRowClasses - extra row classes. OPTIONAL.
     */
    addBootstrapGridClasses(colWidths, extraRowClasses)
    {
        const row = extraRowClasses ? `row ${extraRowClasses}` : 'row';
        this.addAttributesToElement('header', {attribute: 'class', value: row});
        this.assignClassToRow(row);

        let col;
        if (colWidths)
        {
            for (const prop in colWidths)
            {
                if (colWidths[prop].all)
                {
                    col = `col-${colWidths[prop].all}`
                }
                else
                {
                    col = colWidths[prop].xs ? `col-${colWidths[prop].xs}` : 'col';
                    col += colWidths[prop].sm ? ` col-sm-${colWidths[prop].sm}` : ' col-sm';
                    col += colWidths[prop].md ? ` col-md-${colWidths[prop].md}` : ' col-md';
                    col += colWidths[prop].lg ? ` col-lg-${colWidths[prop].lg}` : ' col-lg';
                    col += colWidths[prop].xl ? ` col-xl-${colWidths[prop].xl}` : ' col-xl';
                }

                let offset;
                if (colWidths[prop].offset)
                {
                    offset = colWidths[prop].offset.xs ? ` offset-xs-${colWidths[prop].offset.xs}` : '';
                    offset += colWidths[prop].offset.sm ? ` offset-sm-${colWidths[prop].offset.sm}` : '';
                    offset += colWidths[prop].offset.md ? ` offset-md-${colWidths[prop].offset.md}` : '';
                    offset += colWidths[prop].offset.lg ? ` offset-lg-${colWidths[prop].offset.lg}` : '';
                    offset += colWidths[prop].offset.xl ? ` offset-xl-${colWidths[prop].offset.xl}` : '';
                }
                const value = offset ? col + offset : col;
                this.addAttributesToElement('header', {attribute: 'class', value: value}, prop);
                this.addAttributesToCols({attribute: 'class', value: value}, prop);
            }
        }
        else
        {
            col = 'col';
            this.addAttributesToElement('header', {attribute: 'class', value: col}, 'all');
            this.addAttributesToCols({attribute: 'class', value: col});
        }
    }

// </editor-fold>
}

// ************************************</editor-fold>
// TEST EXPORTS
// ************************************<editor-fold>
/** for JEST tests uncomment exports in:
 * - MergeSort.js
 * - CreateElement.js*
 * - EventDelegator.js
 * - exports AND imports (line 13-15) in this file
 */
// module.exports = {Table};
// export {Table};
